<template>
  <div class="dashboard-workbench">
    <!-- 首页工作台骨架：欢迎区、快捷菜单、统计卡片、任务列表四块组合。 -->
    <section class="workbench-hero">
      <div class="workbench-hero__content">
        <div class="workbench-hero__badge">REPORT WORKBENCH</div>
        <h1 class="workbench-hero__title">您好，欢迎使用智能报告生成平台</h1>
        <p class="workbench-hero__desc">AI 驱动报告自动化，快速发起申请、跟进审核状态，并一键直达当前账号可用功能。</p>
        <div class="workbench-hero__meta">
          <span>{{ userName }}</span>
          <span>{{ isAdminWorkbench ? '管理员视图' : '个人视图' }}</span>
          <span>{{ todayText }}</span>
        </div>
      </div>
      <div class="workbench-hero__actions">
        <el-button
          v-if="applyEntry"
          size="small"
          icon="el-icon-document-add"
          @click="handleQuickMenuClick(applyEntry)"
        >发起报告申请</el-button>
        <el-button
          v-if="reportEntry"
          size="small"
          icon="el-icon-lightning"
          @click="handleQuickMenuClick(reportEntry)"
        >快速生成报告</el-button>
      </div>
      <div class="workbench-hero__orb workbench-hero__orb--one"></div>
      <div class="workbench-hero__orb workbench-hero__orb--two"></div>
    </section>

    <!-- ===== 区块1：常用功能（个性化，支持编辑+排序）仅管理员视图 ===== -->
    <section v-if="isAdminWorkbench" class="workbench-section workbench-section--favorite">
      <div class="workbench-section__header">
        <div>
          <h2>常用功能</h2>
          <p>按你的使用习惯自定义显示的卡片，整体按业务分组归类、同组统一色系。</p>
        </div>
        <el-button
          type="primary"
          size="mini"
          icon="el-icon-setting"
          @click="openFavoriteEdit"
        >编辑常用</el-button>
      </div>
      <!-- 空态：未配置常用 -->
      <div v-if="!favoriteGroups.length" class="workbench-favorite-empty">
        <i class="el-icon-star-off"></i>
        <p>还没有常用功能，点击右上角【编辑常用】添加吧</p>
      </div>
      <!-- 已配置：按业务分组展示，同组同色 -->
      <div v-else class="workbench-favorite-list">
        <div
          v-for="group in favoriteGroups"
          :key="'fav-' + group.key"
          class="workbench-group-row workbench-group-row--favorite"
        >
          <div class="workbench-group-title" :class="`is-${group.tone}`">
            <span class="workbench-group-title__dot"></span>
            <span class="workbench-group-title__text">{{ group.title }}</span>
          </div>
          <div class="workbench-entry-cards">
            <button
              v-for="item in group.menus"
              :key="'fav-card-' + (item.authId || item.authUrl)"
              type="button"
              class="workbench-entry-card"
              :class="`is-${group.tone}`"
              @click="handleQuickMenuClick(item)"
            >
              <span class="workbench-entry-card__icon"><i :class="item.authIcon || 'el-icon-menu'"></i></span>
              <span class="workbench-entry-card__body">
                <strong>{{ item.authName }}</strong>
                <small>{{ item.parentName || '平台功能' }}</small>
              </span>
              <i class="el-icon-arrow-right workbench-entry-card__arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 区块2：功能快捷入口（全量权限菜单，3分组分3行展示）仅管理员视图 ===== -->
    <section v-if="isAdminWorkbench" class="workbench-section">
      <div class="workbench-section__header">
        <div>
          <h2>功能快捷入口</h2>
          <p>按业务分组归类，同组卡片统一色系，根据当前账号权限动态展示。</p>
        </div>
        <span class="workbench-section__count">{{ entryTotalCount }} 个入口</span>
      </div>
      <div v-if="entryGroups.length" class="workbench-entry-layout">
        <div
          v-for="group in entryGroups"
          :key="'entry-' + group.key"
          class="workbench-group-row"
        >
          <div class="workbench-group-title" :class="`is-${group.tone}`">
            <span class="workbench-group-title__dot"></span>
            <span class="workbench-group-title__text">{{ group.title }}</span>
          </div>
          <div class="workbench-entry-cards">
            <button
              v-for="item in group.menus"
              :key="'entry-card-' + (item.authId || item.authUrl)"
              type="button"
              class="workbench-entry-card"
              :class="`is-${group.tone}`"
              @click="handleQuickMenuClick(item)"
            >
              <span class="workbench-entry-card__icon"><i :class="item.authIcon || 'el-icon-menu'"></i></span>
              <span class="workbench-entry-card__body">
                <strong>{{ item.authName }}</strong>
                <small>{{ item.parentName || '平台功能' }}</small>
              </span>
              <i class="el-icon-arrow-right workbench-entry-card__arrow"></i>
            </button>
          </div>
        </div>
      </div>
      <div v-else class="workbench-empty">暂无可用快捷入口</div>
    </section>

    <!-- ===== 编辑常用功能弹框（勾选 + 上移/下移排序） ===== -->
    <el-dialog
      title="编辑常用功能"
      :visible.sync="favoriteEditVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="favorite-edit-dialog"
    >
      <div class="favorite-edit-body">
        <!-- 可选项：按业务分组列出全部权限菜单 -->
        <div class="favorite-edit-section">
          <div class="favorite-edit-section__title">勾选常用功能</div>
          <div
            v-for="group in favoriteOptionsGroups"
            :key="'opt-' + group.key"
            class="favorite-edit-group"
          >
            <div class="favorite-edit-group__title" :class="`is-${group.tone}`">{{ group.title }}</div>
            <el-checkbox-group v-model="favoriteEditIds" class="favorite-edit-group__checks">
              <el-checkbox
                v-for="item in group.menus"
                :key="'opt-item-' + (item.authId || item.authUrl)"
                :label="item.authId"
                class="favorite-edit-check"
              >{{ item.authName }}</el-checkbox>
            </el-checkbox-group>
          </div>
          <div v-if="!favoriteOptionsGroups.length" class="favorite-edit-empty">暂无可选功能</div>
        </div>
        <!-- 已选排序区：上移/下移调整顺序 -->
        <div class="favorite-edit-section">
          <div class="favorite-edit-section__title">
            调整排序
            <span class="favorite-edit-section__hint">已选 {{ favoriteEditIds.length }} 项，点击上移/下移调整顺序</span>
          </div>
          <div v-if="favoriteEditSorted.length" class="favorite-edit-sort">
            <div
              v-for="(item, idx) in favoriteEditSorted"
              :key="'sort-' + (item.authId || idx)"
              class="favorite-edit-sort-item"
            >
              <span class="favorite-edit-sort-item__index">{{ idx + 1 }}</span>
              <span class="favorite-edit-sort-item__name">{{ item.authName }}</span>
              <span class="favorite-edit-sort-item__ops">
                <el-button type="text" size="mini" icon="el-icon-top" :disabled="idx === 0" @click="moveFavorite(item.authId, -1)">上移</el-button>
                <el-button type="text" size="mini" icon="el-icon-bottom" :disabled="idx === favoriteEditSorted.length - 1" @click="moveFavorite(item.authId, 1)">下移</el-button>
                <el-button type="text" size="mini" icon="el-icon-close" style="color:#ef4444;" @click="removeFavorite(item.authId)">移除</el-button>
              </span>
            </div>
          </div>
          <div v-else class="favorite-edit-empty">暂未选择任何功能</div>
        </div>
      </div>
      <div slot="footer" class="favorite-edit-footer">
        <el-button size="small" @click="cancelFavoriteEdit">取消</el-button>
        <el-button type="primary" size="small" :loading="favoriteSaving" @click="saveFavoriteEdit">保存</el-button>
      </div>
    </el-dialog>

    <!-- ===== 管理员统计区 ===== -->
    <template v-if="isAdminWorkbench">
      <section class="workbench-section workbench-section--data">
        <div class="workbench-section__header">
          <div>
            <h2>全站报告包统计</h2>
            <p>统计全部报告包申请记录的整体数据。</p>
          </div>
          <div class="workbench-section__filters">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              size="small"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              style="width: 260px;"
              @change="handleDateChange"
            />
            <el-button size="mini" icon="el-icon-refresh" :loading="statsLoading" @click="loadStatistics">刷新</el-button>
          </div>
        </div>

        <div v-if="statsLoading" class="workbench-status-grid" :class="isAdminWorkbench ? 'is-admin-grid' : ''">
          <div v-for="item in 6" :key="item" class="workbench-status-card is-loading"></div>
        </div>
        <div v-else-if="statsError" class="workbench-empty workbench-empty--error">
          {{ statsError }}
        </div>
        <div v-else class="workbench-status-grid is-admin-grid">
          <div
            v-for="item in adminStatsCards"
            :key="item.key"
            class="workbench-status-card"
            :class="[`is-${item.key}`, { 'is-clickable': item.path }]"
            :title="item.path ? '点击查看「' + item.label + '」' : ''"
            @click="handleStatCardClick(item)"
          >
            <div class="workbench-status-card__icon"><i :class="item.icon"></i></div>
            <div class="workbench-status-card__label">{{ item.label }}</div>
            <div class="workbench-status-card__value">{{ item.value }}<span v-if="item.unit" class="workbench-status-card__unit">{{ item.unit }}</span></div>
            <div class="workbench-status-card__desc">{{ item.desc }}</div>
          </div>
        </div>
      </section>

      <!-- ===== 待办提醒 ===== -->
      <section class="workbench-section">
        <div class="workbench-section__header">
          <div>
            <h2>待办提醒</h2>
            <p>今日待交付、逾期未交付及待审核的申请记录。</p>
          </div>
          <el-button size="mini" icon="el-icon-refresh" :loading="todoLoading" @click="loadTodo">刷新</el-button>
        </div>
        <div class="workbench-todo-layout">
          <!-- 左侧：数字指标 -->
          <div class="workbench-todo-cards">
            <div class="workbench-todo-card is-today">
              <i class="el-icon-time"></i>
              <span class="workbench-todo-card__num">{{ todoData.countToday || 0 }}</span>
              <span class="workbench-todo-card__text">今日待交付</span>
            </div>
            <div class="workbench-todo-card is-overdue">
              <i class="el-icon-warning"></i>
              <span class="workbench-todo-card__num">{{ todoData.countOverdue || 0 }}</span>
              <span class="workbench-todo-card__text">逾期未交付</span>
            </div>
          </div>
          <!-- 右侧：待审核列表 -->
          <div class="workbench-todo-table">
            <el-table
              v-if="todoData.rtReportModelApplyList && todoData.rtReportModelApplyList.length"
              :data="todoData.rtReportModelApplyList.slice(0, 5)"
              border
              stripe
              size="small"
              style="width: 100%;"
            >
              <el-table-column prop="rfiOrderNum" label="销售订单号" width="130" show-overflow-tooltip />
              <el-table-column prop="applyusername" label="申请人" width="90" align="center" show-overflow-tooltip />
              <el-table-column prop="applyRuleNameCustomer" label="客户名称" min-width="120" show-overflow-tooltip />
              <el-table-column prop="applyRuleNameCourse" label="课程名称" min-width="140" show-overflow-tooltip />
              <el-table-column prop="rrmaCourseType" label="课程类型" width="90" align="center" />
              <el-table-column label="培训日期" width="170" align="center">
                <template slot-scope="{ row }">
                  {{ row.applyRuleNameDateStart || '-' }} ~ {{ row.applyRuleNameDateEnd || '-' }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="80" align="center">
                <template slot-scope="{ row }">
                  <el-button type="text" size="small" @click="handleViewTodo(row)">审核</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div v-else class="workbench-empty workbench-empty--tasks">暂无待审核记录</div>
          </div>
        </div>
      </section>

      <!-- ===== 效率统计 + 排行榜 ===== -->
      <div class="workbench-duo-layout">
        <section class="workbench-section">
          <div class="workbench-section__header">
            <div>
              <h2>效率统计</h2>
              <p>全平台平均审批与报告生成耗时。</p>
            </div>
          </div>
          <div ref="efficiencyChart" class="workbench-chart-container"></div>
        </section>
        <section class="workbench-section workbench-section--rank">
          <div class="workbench-section__header">
            <div>
              <h2>模板使用排行</h2>
              <p>按申请、导出、修正次数排名的模板排行。</p>
            </div>
          </div>
          <el-tabs v-model="rankTab" type="border-card" class="workbench-rank-tabs">
            <el-tab-pane label="申请次数" name="apply">
              <div class="workbench-rank-list">
                <div
                  v-for="(item, idx) in rankData.rankApply.slice(0, 20)"
                  :key="'a-' + item.rrmId + '-' + idx"
                  class="workbench-rank-item"
                >
                  <span class="workbench-rank-item__index" :class="idx < 3 ? 'is-top is-top-' + (idx + 1) : ''">{{ idx + 1 }}</span>
                  <span class="workbench-rank-item__name" :title="item.modelName">{{ item.modelName }}</span>
                  <span class="workbench-rank-item__count">{{ item.nums }}次</span>
                </div>
                <div v-if="!rankData.rankApply.length" class="workbench-empty workbench-empty--tasks">暂无数据</div>
                <div v-if="rankData.rankApply.length > 20" class="workbench-rank-more">
                  <el-button type="text" icon="el-icon-more" @click="openRankDialog('apply')">查看全部（共{{ rankData.rankApply.length }}个模板）</el-button>
                </div>
              </div>
            </el-tab-pane>
            <el-tab-pane label="导出次数" name="export">
              <div class="workbench-rank-list">
                <div
                  v-for="(item, idx) in rankData.rankExport.slice(0, 20)"
                  :key="'e-' + item.rrmId + '-' + idx"
                  class="workbench-rank-item"
                >
                  <span class="workbench-rank-item__index" :class="idx < 3 ? 'is-top is-top-' + (idx + 1) : ''">{{ idx + 1 }}</span>
                  <span class="workbench-rank-item__name" :title="item.modelName">{{ item.modelName }}</span>
                  <span class="workbench-rank-item__count">{{ item.nums }}次</span>
                </div>
                <div v-if="!rankData.rankExport.length" class="workbench-empty workbench-empty--tasks">暂无数据</div>
                <div v-if="rankData.rankExport.length > 20" class="workbench-rank-more">
                  <el-button type="text" icon="el-icon-more" @click="openRankDialog('export')">查看全部（共{{ rankData.rankExport.length }}个模板）</el-button>
                </div>
              </div>
            </el-tab-pane>
            <el-tab-pane label="修正次数" name="correct">
              <div class="workbench-rank-list">
                <div
                  v-for="(item, idx) in rankData.rankCorrect.slice(0, 20)"
                  :key="'c-' + item.rrmId + '-' + idx"
                  class="workbench-rank-item"
                >
                  <span class="workbench-rank-item__index" :class="idx < 3 ? 'is-top is-top-' + (idx + 1) : ''">{{ idx + 1 }}</span>
                  <span class="workbench-rank-item__name" :title="item.modelName">{{ item.modelName }}</span>
                  <span class="workbench-rank-item__count">{{ item.nums }}次</span>
                </div>
                <div v-if="!rankData.rankCorrect.length" class="workbench-empty workbench-empty--tasks">暂无数据</div>
                <div v-if="rankData.rankCorrect.length > 20" class="workbench-rank-more">
                  <el-button type="text" icon="el-icon-more" @click="openRankDialog('correct')">查看全部（共{{ rankData.rankCorrect.length }}个模板）</el-button>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </section>
      </div>

      <!-- 排行榜弹框 -->
      <el-dialog
        :title="rankDialogTitle"
        :visible.sync="rankDialogVisible"
        width="820px"
        append-to-body
        :close-on-click-modal="true"
      >
        <el-table :data="rankDialogData" border stripe size="small" max-height="500">
          <el-table-column type="index" label="排名" width="80" align="center">
            <template slot-scope="{ $index }">
              <span :class="$index < 3 ? 'rank-dialog-top' : ''">{{ $index + 1 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="modelName" label="模板名称" min-width="360" show-overflow-tooltip />
          <el-table-column prop="nums" label="次数" width="100" align="center" />
        </el-table>
      </el-dialog>
    </template>

    <!-- ===== 用户统计区 + 任务列表 ===== -->
    <template v-else>
      <section class="workbench-section workbench-section--data">
        <div class="workbench-section__header">
          <div>
            <h2>我的报告包数据</h2>
            <p>统计当前账号提交的报告包申请记录。</p>
          </div>
          <div class="workbench-section__filters">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              size="small"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              style="width: 260px;"
              @change="handleDateChange"
            />
            <el-button size="mini" icon="el-icon-refresh" :loading="statsLoading" @click="loadStatistics">刷新</el-button>
          </div>
        </div>

        <div v-if="statsLoading" class="workbench-status-grid">
          <div v-for="item in 6" :key="item" class="workbench-status-card is-loading"></div>
        </div>
        <div v-else-if="statsError" class="workbench-empty workbench-empty--error">
          {{ statsError }}
        </div>
        <div v-else class="workbench-status-grid">
          <div
            v-for="item in userStatsCards"
            :key="item.key"
            class="workbench-status-card"
            :class="[`is-${item.key}`, { 'is-clickable': item.path }]"
            :title="item.path ? '点击查看「' + item.label + '」' : ''"
            @click="handleStatCardClick(item)"
          >
            <div class="workbench-status-card__icon"><i :class="item.icon"></i></div>
            <div class="workbench-status-card__label">{{ item.label }}</div>
            <div class="workbench-status-card__value">{{ item.value }}</div>
            <div class="workbench-status-card__desc">{{ item.desc }}</div>
          </div>
        </div>
      </section>

      <!-- 任务列表区（仅用户视图） -->
      <section class="workbench-section workbench-section--tasks">
        <div class="workbench-section__header">
          <div>
            <h2>任务列表</h2>
            <p>当前账号的报告包申请任务，按截止时间排列。</p>
          </div>
          <span v-if="taskList.length" class="workbench-section__count">{{ taskList.length }} 项任务</span>
        </div>
        <div v-if="taskList.length" class="workbench-task-table-wrap">
          <el-table :data="taskList" border stripe size="small" style="width: 100%;">
            <el-table-column prop="rfiOrderNum" label="订单号" width="140" show-overflow-tooltip />
            <el-table-column prop="rrmaCourseType" label="课程类型" width="120" align="center" />
            <el-table-column prop="applyRuleNameCustomer" label="客户名称" min-width="180" show-overflow-tooltip />
            <el-table-column prop="applyRuleNameCourse" label="课程名称" min-width="200" show-overflow-tooltip />
            <el-table-column label="培训日期" width="210" align="center">
              <template slot-scope="{ row }">
                {{ row.applyRuleNameDateStart || '-' }} ~ {{ row.applyRuleNameDateEnd || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="剩余时间" width="120" align="center">
              <template slot-scope="{ row }">
                <el-tag v-if="getDeadlineInfo(row.applyRuleNameDateDeadline).type === 'info'" type="info" size="mini">-</el-tag>
                <el-tag v-else :type="getDeadlineInfo(row.applyRuleNameDateDeadline).type" size="mini" effect="dark">
                  {{ getDeadlineInfo(row.applyRuleNameDateDeadline).text }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="当前状态" width="130" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="getTaskStatus(row).type" size="mini">{{ getTaskStatus(row).label }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" @click="handleViewTask(row)">查看</el-button>
                <el-button v-if="row.rrmaStatus === 3" type="text" size="small" style="color: #10b981;" @click="handleGenerateTask(row)">继续生成</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else class="workbench-empty workbench-empty--tasks">
          <template v-if="statsLoading">加载中...</template>
          <template v-else>暂无任务数据</template>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { getRtStatisticsModelApplyUserAll, getRtStatisticsModelApplyAdmin, getRtStatisticsModelApplyTodo, getRtStatisticsModelApplyRank, getRtStatisticsModelApplyEfficiency, getRtUserModelCollect, updateRtUserModelCollect } from '@/api/api'
import { normalizeMenuPath } from '@/utils/menu'
// 首页看板：基于接口菜单树的分组 + 分组配色 + 常用功能本地配置
import { buildEntryGroups, flattenGroupMenus, regroupMenus, getFavoriteIds, setFavoriteIds } from '@/utils/dashboardGroups'

// 用户统计卡片配置
// myReport 卡片：值 = 待发起 + 已发起 + 待审核 之和，点击跳转我的报告（置于首位，突出汇总）
const USER_STATS_TEMPLATE = [
  { key: 'myReport', field: 'countMyReport', sumFields: ['countUnSubmit', 'countSubmit', 'countAudit'], label: '我的报告', icon: 'el-icon-suitcase', desc: '待发起 + 已发起 + 待审核 总和', path: '/reportApply/reportApplyList' },
  { key: 'unSubmit', field: 'countUnSubmit', label: '待发起', icon: 'el-icon-edit-outline', desc: '填写但尚未提交的申请' },
  { key: 'submit', field: 'countSubmit', label: '已发起', icon: 'el-icon-s-promotion', desc: '已提交的申请总数' },
  { key: 'audit', field: 'countAudit', label: '待审核', icon: 'el-icon-time', desc: '等待管理员审核处理' },
  { key: 'auditModify', field: 'countAuditModify', label: '被修改', icon: 'el-icon-warning-outline', desc: '审核中被管理员修改' },
  { key: 'generate', field: 'countGenerate', label: '已生成下载', icon: 'el-icon-folder-checked', desc: '已生成并下载报告正文' }
]

// 管理员统计卡片配置（对应后端 getRtStatisticsModelApplyAdmin 预期返回字段）
// path 字段：卡片点击快速跳转目标；前5个统计→我的报告，用户反馈→反馈管理
const ADMIN_STATS_TEMPLATE = [
  { key: 'adminUnSubmit', field: 'countUnSubmit', label: '待发起', icon: 'el-icon-edit-outline', desc: '全公司待提交的申请', unit: '', path: '/reportApply/reportApplyList' },
  { key: 'adminSubmit', field: 'countSubmit', label: '已申请', icon: 'el-icon-s-promotion', desc: '全公司已提交的申请总数', unit: '', path: '/reportApply/reportApplyList' },
  { key: 'adminAudit', field: 'countAudit', label: '待审核', icon: 'el-icon-time', desc: '全公司等待审核的申请', unit: '', path: '/reportApply/reportApplyList' },
  { key: 'adminGenerate', field: 'countGenerate', label: '已下载报告', icon: 'el-icon-folder-checked', desc: '全公司已生成并下载报告正文', unit: '', path: '/reportApply/reportApplyList' },
  { key: 'adminAvgTime', field: 'countGenerateLength', label: '平均生成时长', icon: 'el-icon-timer', desc: '从上传资料到下载报告的平均耗时', unit: ' 分钟', path: '/reportApply/reportApplyList' },
  { key: 'adminFeedback', field: 'countFeedback', label: '用户反馈', icon: 'el-icon-chat-line-round', desc: '用户意见反馈总数', unit: '', path: '/feedback/feedbackAdmin' }
]

export default {
  name: 'DashboardPage',
  data () {
    return {
      statsLoading: false,
      statsError: '',
      todoLoading: false,
      // 用户视图数据
      userStatsCards: [],
      // 管理员视图数据
      adminStatsCards: [],
      // 效率统计
      efficiencyData: { avgApplyApproveLength: 0, avgGenerateLength: 0 },
      // 模板排行
      rankData: { rankApply: [], rankExport: [], rankCorrect: [] },
      rankTab: 'apply',
      rankDialogVisible: false,
      rankDialogTitle: '',
      rankDialogData: [],
      // 待办提醒
      todoData: { countToday: 0, countOverdue: 0, rtReportModelApplyList: [] },
      // 共用
      dateRange: null,
      taskList: [],
      // 常用功能：已保存的常用 authId 有序数组
      favoriteIds: [],
      // 当前用户收藏记录 id（后端主键，保存时回传）
      favoriteRumcId: null,
      // 常用功能保存中（防止重复提交）
      favoriteSaving: false,
      // 编辑常用弹框：显隐 + 编辑中的临时勾选（顺序即排序）
      favoriteEditVisible: false,
      favoriteEditIds: [],
      // 日期快捷选项
      pickerOptions: {
        shortcuts: [
          { text: '最近一周', onClick: picker => { const end = new Date(); const start = new Date(); start.setTime(start.getTime() - 7 * 24 * 3600 * 1000); picker.$emit('pick', [start, end]) } },
          { text: '最近一个月', onClick: picker => { const end = new Date(); const start = new Date(); start.setMonth(start.getMonth() - 1); picker.$emit('pick', [start, end]) } },
          { text: '最近三个月', onClick: picker => { const end = new Date(); const start = new Date(); start.setMonth(start.getMonth() - 3); picker.$emit('pick', [start, end]) } }
        ]
      }
    }
  },
  computed: {
    userInfo () {
      return this.$store.state.user.userInfo || {}
    },
    userName () {
      return this.userInfo.auName || this.userInfo.userName || '用户'
    },
    menuTree () {
      return this.$store.state.permission.menuTree || []
    },
    flatMenus () {
      return this.collectQuickMenus(this.menuTree)
    },
    quickMenus () {
      return this.flatMenus.filter(item => normalizeMenuPath(item.authUrl) !== '/dashboard')
    },
    // 当前账号标识（用于常用配置按账号隔离存储）
    userKey () {
      const info = this.userInfo || {}
      return String(info.auName || info.userName || info.auId || 'default')
    },
    // 功能快捷入口分组：严格基于接口菜单树目录层级生成
    // 顶层目录 → 其下叶子为一组；顶层叶子 → 自身单独成组
    entryGroups () {
      return buildEntryGroups(this.menuTree)
    },
    // 全部可点击菜单（带分组标记），用于常用功能查找映射
    entryMenus () {
      return flattenGroupMenus(this.entryGroups)
    },
    // 功能快捷入口总数
    entryTotalCount () {
      return this.entryMenus.length
    },
    // 常用功能：按 favoriteIds 顺序映射到实际菜单（过滤失效项）
    favoriteMenus () {
      if (!this.favoriteIds.length) return []
      const map = {}
      this.entryMenus.forEach(item => {
        if (item.authId !== undefined && item.authId !== null) map[item.authId] = item
      })
      return this.favoriteIds.map(id => map[id]).filter(Boolean)
    },
    // 常用功能：按其所属分组重新归类展示（保持 entryGroups 分组顺序）
    favoriteGroups () {
      return regroupMenus(this.favoriteMenus, this.entryGroups)
    },
    // 编辑弹框：可选菜单（全量权限菜单按分组，用于 checkbox 列表）
    favoriteOptionsGroups () {
      return this.entryGroups
    },
    // 编辑弹框：已勾选项映射成菜单对象（保持 favoriteEditIds 顺序，用于排序区）
    favoriteEditSorted () {
      if (!this.favoriteEditIds.length) return []
      const map = {}
      this.entryMenus.forEach(item => {
        if (item.authId !== undefined && item.authId !== null) map[item.authId] = item
      })
      return this.favoriteEditIds.map(id => map[id]).filter(Boolean)
    },
    // 是否管理员视图（auPost=1 为系统管理员）
    isAdminWorkbench () {
      const post = Number(this.userInfo.auPost || this.userInfo.post || this.userInfo.userPost || 0)
      return post === 1
    },
    todayText () {
      const now = new Date()
      const weekMap = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return `${now.getFullYear()}-${this.padDate(now.getMonth() + 1)}-${this.padDate(now.getDate())} ${weekMap[now.getDay()]}`
    },
    applyEntry () {
      return this.quickMenus.find(item => normalizeMenuPath(item.authUrl) === '/reportApply/reportApplyList') || null
    },
    reportEntry () {
      return this.quickMenus.find(item => normalizeMenuPath(item.authUrl) === '/report/create') ||
        this.quickMenus.find(item => normalizeMenuPath(item.authUrl) === '/report/list') ||
        null
    }
  },
  watch: {
    // 管理员/用户身份切换时重新加载
    isAdminWorkbench () {
      this.loadStatistics()
    },
    // 账号切换时刷新常用功能配置（按账号隔离）
    userKey () {
      this.initFavorites()
    }
  },
  created () {
    this.loadStatistics()
    // 初始化当前账号的常用功能配置（按 userKey 从 localStorage 读取，并过滤失效菜单）
    this.initFavorites()
  },
  mounted () {
    // 窗口尺寸变化时自适应调整图表
    this._resizeHandler = () => {
      this.efficiencyChartInstance && this.efficiencyChartInstance.resize()
    }
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this._resizeHandler)
    if (this.efficiencyChartInstance) {
      this.efficiencyChartInstance.dispose()
      this.efficiencyChartInstance = null
    }
  },
  methods: {
    padDate (value) {
      return String(value).padStart(2, '0')
    },
    collectQuickMenus (tree, parentName = '') {
      const result = []
      ;(tree || []).forEach(item => {
        if (!item || Number(item.authType) === 2) return
        const currentParentName = parentName || item.authName || ''
        if (item.authUrl) {
          result.push({ ...item, parentName })
        }
        if (Array.isArray(item.children) && item.children.length) {
          result.push(...this.collectQuickMenus(item.children, currentParentName))
        }
      })
      return result
    },
    handleQuickMenuClick (item) {
      const targetPath = normalizeMenuPath(item && item.authUrl)
      if (!targetPath) return
      if (this.$route.path === targetPath) return
      this.$router.push(targetPath)
    },

    // 管理员统计卡点击快速跳转（仅配置了 path 的卡片可跳转）
    handleStatCardClick (item) {
      const targetPath = normalizeMenuPath(item && item.path)
      if (!targetPath) return
      if (this.$route.path === targetPath) return
      this.$router.push(targetPath).catch(() => {})
    },

    // ============ 常用功能（个性化配置：勾选 + 排序，后端持久化 + 本地降级） ============
    // 从后端读取当前账号常用功能（不存在后端会自动新增）；接口异常时降级到 localStorage
    async initFavorites () {
      // 先用本地缓存做兜底展示，避免后端慢时空白
      this.favoriteIds = getFavoriteIds(this.userKey, this.entryMenus)
      try {
        const res = await getRtUserModelCollect()
        const record = (res && res.data) || {}
        this.favoriteRumcId = record.rumcId || null
        // rumcModel 为逗号分隔的 authId 字符串，解析为有序数组并过滤当前已失效菜单
        const ids = this.parseFavoriteModel(record.rumcModel)
        // 后端有数据则以后端为准；后端为空时保留本地兜底（兼容历史本地用户首次迁移）
        if (ids.length) {
          this.favoriteIds = ids
          // 同步回写本地缓存（保持两端一致）
          setFavoriteIds(this.userKey, ids)
        } else if (this.favoriteIds.length) {
          // 本地有数据但后端空：自动迁移到后端（静默保存，不弹提示）
          this.silentMigrateLocalFavorites()
        }
      } catch (e) {
        // 接口异常：保持 localStorage 兜底数据，不抛错
        this.favoriteRumcId = null
      }
    },
    // 解析后端 rumcModel（逗号分隔字符串）为 authId 有序数组，并过滤当前权限中已失效项
    parseFavoriteModel (rumcModel) {
      if (!rumcModel) return []
      const raw = String(rumcModel).split(',').map(s => s.trim()).filter(Boolean)
      // 转为数字（authId 为数字），转换失败的原样保留
      const ids = raw.map(s => {
        const n = Number(s)
        return Number.isNaN(n) ? s : n
      })
      // 过滤当前菜单中已不存在的 authId
      if (this.entryMenus && this.entryMenus.length) {
        const validSet = new Set(this.entryMenus.map(m => m.authId))
        return ids.filter(id => validSet.has(id))
      }
      return ids
    },
    // 本地有数据、后端为空时静默迁移到后端（避免老用户本地配置丢失）
    async silentMigrateLocalFavorites () {
      if (!this.favoriteRumcId || !this.favoriteIds.length) return
      try {
        await updateRtUserModelCollect({
          rumcId: this.favoriteRumcId,
          rumcModel: this.favoriteIds.join(',')
        })
      } catch (e) {
        // 迁移失败不影响使用，本地缓存仍在
      }
    },
    // 打开编辑弹框：用当前已保存配置初始化临时勾选
    openFavoriteEdit () {
      this.favoriteEditIds = [...this.favoriteIds]
      this.favoriteEditVisible = true
    },
    // 排序区直接移除某项
    removeFavorite (id) {
      this.favoriteEditIds = this.favoriteEditIds.filter(x => x !== id)
    },
    // 上移(-1) / 下移(+1)
    moveFavorite (id, dir) {
      const idx = this.favoriteEditIds.indexOf(id)
      if (idx < 0) return
      const target = idx + dir
      if (target < 0 || target >= this.favoriteEditIds.length) return
      const arr = [...this.favoriteEditIds]
      const tmp = arr[idx]
      arr[idx] = arr[target]
      arr[target] = tmp
      this.favoriteEditIds = arr
    },
    // 保存：调后端持久化 + 同步本地缓存，刷新展示
    async saveFavoriteEdit () {
      // 后端要求模块不能为空：清空时不允许保存，提示用户
      if (!this.favoriteEditIds.length) {
        this.$message.warning('请至少保留一个常用功能')
        return
      }
      if (!this.favoriteRumcId) {
        // 记录 id 缺失（可能初始化未完成），尝试重新拉取一次
        try {
          const res = await getRtUserModelCollect()
          this.favoriteRumcId = (res && res.data && res.data.rumcId) || null
        } catch (e) {
          this.favoriteRumcId = null
        }
        if (!this.favoriteRumcId) {
          this.$message.error('保存失败，请刷新页面重试')
          return
        }
      }
      this.favoriteSaving = true
      try {
        await updateRtUserModelCollect({
          rumcId: this.favoriteRumcId,
          rumcModel: this.favoriteEditIds.join(',')
        })
        // 后端保存成功后同步本地缓存 + 展示数据
        this.favoriteIds = setFavoriteIds(this.userKey, this.favoriteEditIds)
        this.favoriteEditVisible = false
        this.$message.success('常用功能已保存')
      } catch (e) {
        this.$message.error(e.message || '保存失败，请稍后重试')
      } finally {
        this.favoriteSaving = false
      }
    },
    // 取消：仅关闭弹框，不持久化
    cancelFavoriteEdit () {
      this.favoriteEditVisible = false
    },

    // ============ 统计数据加载（区分管理员/用户） ============
    async loadStatistics () {
      this.statsLoading = true
      this.statsError = ''
      try {
        const params = {}
        if (this.dateRange && this.dateRange.length === 2) {
          params.applyRuleNameDateStart = this.dateRange[0]
          params.applyRuleNameDateEnd = this.dateRange[1]
        }
        if (this.isAdminWorkbench) {
          await this.loadAdminStatistics(params)
        } else {
          await this.loadUserStatistics(params)
        }
      } catch (error) {
        this.userStatsCards = this.buildCards(USER_STATS_TEMPLATE, {})
        this.adminStatsCards = this.buildCards(ADMIN_STATS_TEMPLATE, {})
        this.taskList = []
        this.statsError = '统计数据暂不可用，请稍后刷新'
      } finally {
        this.statsLoading = false
      }
    },
    // 用户端统计
    async loadUserStatistics (params) {
      const res = await getRtStatisticsModelApplyUserAll(params)
      const data = (res && res.data) || {}
      this.userStatsCards = this.buildCards(USER_STATS_TEMPLATE, data)
      this.taskList = Array.isArray(data.rtReportModelApplyUserTasks) ? data.rtReportModelApplyUserTasks : []
    },
    // 管理员端统计
    async loadAdminStatistics (params) {
      const res = await getRtStatisticsModelApplyAdmin(params)
      const data = (res && res.data) || {}
      this.adminStatsCards = this.buildCards(ADMIN_STATS_TEMPLATE, data)
      this.taskList = []
      // 并行加载效率、排行、待办数据（不阻塞主卡片渲染）
      this.loadEfficiency()
      this.loadRank()
      this.loadTodo()
    },
    // 通用卡片构建
    buildCards (template, data) {
      return template.map(tpl => {
        // myReport 等 sumFields 卡片：对指定字段求和
        if (Array.isArray(tpl.sumFields) && tpl.sumFields.length) {
          const sum = tpl.sumFields.reduce((acc, f) => acc + (Number(data[f]) || 0), 0)
          return { ...tpl, value: sum }
        }
        // countGenerateLength 后端返回秒，前端显示分钟
        if (tpl.field === 'countGenerateLength') {
          const seconds = Number(data[tpl.field]) || 0
          const minutes = seconds > 0 ? (seconds / 60).toFixed(1) : '0'
          return { ...tpl, value: minutes }
        }
        return { ...tpl, value: Number(data[tpl.field] || 0) }
      })
    },
    handleDateChange () {
      this.loadStatistics()
    },

    // ============ 截止时间状态判定（红色=今天截止，橙色=剩余1天） ============
    getDeadlineInfo (deadline) {
      if (!deadline) return { type: 'info', text: '-' }
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const deadlineDate = new Date(deadline)
      deadlineDate.setHours(0, 0, 0, 0)
      const diffDays = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24))
      if (diffDays < 0) return { type: 'danger', text: '已逾期' }
      if (diffDays === 0) return { type: 'danger', text: '今天截止' }
      if (diffDays === 1) return { type: 'warning', text: '剩余 1 天' }
      if (diffDays <= 3) return { type: 'warning', text: '剩余 ' + diffDays + ' 天' }
      return { type: 'success', text: deadline }
    },

    // ============ 任务状态映射（含已逾期、管理员已修正） ============
    getTaskStatus (row) {
      // 已逾期：截止日期已过且未完成
      if (row.applyRuleNameDateDeadline) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const dl = new Date(row.applyRuleNameDateDeadline)
        dl.setHours(0, 0, 0, 0)
        if (dl < today && row.rrmaStatus !== 4) {
          return { label: '已逾期', type: 'danger' }
        }
      }
      if (row.rrmaStatus === 1) return { label: '待发起', type: 'warning' }
      if (row.rrmaStatus === 2) return { label: '待审核', type: '' }
      if (row.rrmaStatus === 4) return { label: '已完成', type: 'success' }
      if (row.rrmaStatus === 3) {
        if (row.rrmaUpdateAdmin === 2) return { label: '管理员已修正', type: 'danger' }
        return { label: '审核通过', type: 'success' }
      }
      return { label: '未知', type: 'info' }
    },

    // ============ 任务列表操作 ============
    handleViewTask (row) {
      if (!row.rrmaId) return
      this.$router.push({ path: '/reportApply/reportApplyList' })
    },
    // 继续生成报告（跳转到工作流页面）
    handleGenerateTask (row) {
      if (!row.rrmaId) return
      this.$router.push({
        path: '/reportApply/reportApplyWorkflow',
        query: { mode: 'generate', rrmaId: row.rrmaId }
      }).catch(() => {})
    },
    // 跳转到待审核详情
    handleViewTodo (row) {
      if (!row.rrmaId) return
      this.$router.push({ path: '/reportApply/reportApplyAdmin' })
    },

    // ============ 管理员端统计接口对接 ============

    // 秒数格式化为可读时间
    formatSeconds (seconds) {
      if (!seconds || seconds <= 0) return '0秒'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = Math.round(seconds % 60)
      if (h > 0) return h + '小时' + (m > 0 ? m + '分' : '')
      if (m > 0) return m + '分' + (s > 0 ? s + '秒' : '')
      return s + '秒'
    },

    // 待办提醒
    async loadTodo () {
      this.todoLoading = true
      try {
        const res = await getRtStatisticsModelApplyTodo()
        const data = (res && res.data) || {}
        this.todoData = {
          countToday: data.countToday || 0,
          countOverdue: data.countOverdue || 0,
          rtReportModelApplyList: Array.isArray(data.rtReportModelApplyList)
            ? data.rtReportModelApplyList : []
        }
      } catch (e) {
        console.error('待办提醒加载失败', e)
      } finally {
        this.todoLoading = false
      }
    },

    // 效率统计
    async loadEfficiency () {
      try {
        const res = await getRtStatisticsModelApplyEfficiency()
        const data = (res && res.data) || {}
        this.efficiencyData = {
          avgApplyApproveLength: data.avgApplyApproveLength || 0,
          avgGenerateLength: data.avgGenerateLength || 0
        }
        this.$nextTick(() => this.initEfficiencyChart())
      } catch (e) {
        console.error('效率统计加载失败', e)
      }
    },

    // 模板使用排行榜
    async loadRank () {
      try {
        const res = await getRtStatisticsModelApplyRank()
        const data = (res && res.data) || {}
        this.rankData = {
          rankApply: Array.isArray(data.rankApply) ? data.rankApply : [],
          rankExport: Array.isArray(data.rankExport) ? data.rankExport : [],
          rankCorrect: Array.isArray(data.rankCorrect) ? data.rankCorrect : []
        }
      } catch (e) {
        console.error('排行榜加载失败', e)
      }
    },

    // 打开排行榜弹框（表格展示全部数据）
    openRankDialog (type) {
      const titleMap = {
        apply: '申请次数排行榜',
        export: '导出次数排行榜',
        correct: '修正次数排行榜'
      }
      this.rankDialogTitle = titleMap[type] || '排行榜'
      this.rankDialogData = this.rankData['rank' + type.charAt(0).toUpperCase() + type.slice(1)] || []
      this.rankDialogVisible = true
    },

    // 初始化效率统计图表（双仪表盘）
    initEfficiencyChart () {
      const el = this.$refs.efficiencyChart
      if (!el) return
      // 若已有实例先销毁
      if (this.efficiencyChartInstance) {
        this.efficiencyChartInstance.dispose()
      }
      const chart = this.$echarts.init(el)
      this.efficiencyChartInstance = chart

      const { avgApplyApproveLength, avgGenerateLength } = this.efficiencyData
      // 仪表盘上限取两值较大者 ×1.3，最少 300 秒（5分钟）
      const rawMax = Math.max(avgApplyApproveLength, avgGenerateLength, 300)
      const gaugeMax = Math.ceil(rawMax * 1.3 / 60) * 60

      chart.setOption({
        tooltip: { trigger: 'item' },
        series: [
          {
            name: '审批耗时',
            type: 'gauge',
            center: ['25%', '55%'],
            radius: '75%',
            startAngle: 210,
            endAngle: -30,
            min: 0,
            max: gaugeMax,
            progress: { show: true, width: 14, roundCap: true },
            axisLine: { lineStyle: { width: 14, color: [[1, '#e2e8f0']] } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            pointer: { show: false },
            title: {
              show: true,
              offsetCenter: [0, '80%'],
              fontSize: 13,
              color: '#64748b',
              fontWeight: 700
            },
            detail: {
              valueAnimation: true,
              formatter: (val) => this.formatSeconds(val),
              fontSize: 18,
              fontWeight: 900,
              color: '#0f172a',
              offsetCenter: [0, '48%']
            },
            data: [{ value: avgApplyApproveLength, name: '平均审批耗时' }],
            itemStyle: { color: '#2563eb' }
          },
          {
            name: '生成耗时',
            type: 'gauge',
            center: ['75%', '55%'],
            radius: '75%',
            startAngle: 210,
            endAngle: -30,
            min: 0,
            max: gaugeMax,
            progress: { show: true, width: 14, roundCap: true },
            axisLine: { lineStyle: { width: 14, color: [[1, '#e2e8f0']] } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            pointer: { show: false },
            title: {
              show: true,
              offsetCenter: [0, '80%'],
              fontSize: 13,
              color: '#64748b',
              fontWeight: 700
            },
            detail: {
              valueAnimation: true,
              formatter: (val) => this.formatSeconds(val),
              fontSize: 18,
              fontWeight: 900,
              color: '#0f172a',
              offsetCenter: [0, '48%']
            },
            data: [{ value: avgGenerateLength, name: '平均生成耗时' }],
            itemStyle: { color: '#10b981' }
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-workbench {
  min-height: 100%;
  padding: 8px 18px 26px;
  color: #0f172a;
  background:
    radial-gradient(circle at 12% 6%, rgba(37, 99, 235, 0.08), transparent 26%),
    linear-gradient(180deg, #f6f9fc 0%, #eef3f8 100%);
}

.workbench-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 26px;
  min-height: 210px;
  padding: 34px 38px;
  border-radius: 26px;
  color: #ffffff;
  background:
    radial-gradient(circle at 92% 16%, rgba(255, 255, 255, 0.22), transparent 25%),
    linear-gradient(128deg, #1e40af 0%, #2563eb 46%, #0891b2 100%);
  box-shadow:
    0 24px 54px rgba(37, 99, 235, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.28);
  overflow: hidden;
}

.workbench-hero__content,
.workbench-hero__actions {
  position: relative;
  z-index: 2;
}

.workbench-hero__badge {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  color: #dbeafe;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.workbench-hero__title {
  margin: 16px 0 0;
  font-size: 32px;
  font-weight: 900;
  letter-spacing: -0.02em;
}

.workbench-hero__desc {
  max-width: 680px;
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.86);
  font-size: 15px;
  line-height: 1.8;
}

.workbench-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;

  span {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 0 12px;
    border-radius: 999px;
    color: #eff6ff;
    background: rgba(15, 23, 42, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 13px;
    font-weight: 700;
  }
}

.workbench-hero__actions {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 172px;

  .el-button {
    justify-content: center;
    min-height: 42px;
    border-radius: 14px !important;
    color: #ffffff !important;
    border-color: rgba(255, 255, 255, 0.26) !important;
    background: rgba(255, 255, 255, 0.14) !important;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.16) !important;
    backdrop-filter: blur(8px);
  }

  .el-button:first-child {
    color: #1d4ed8 !important;
    background: #ffffff !important;
  }
}

.workbench-hero__orb {
  position: absolute;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
}

.workbench-hero__orb--one {
  right: -52px;
  top: -74px;
  width: 300px;
  height: 300px;
}

.workbench-hero__orb--two {
  right: 190px;
  bottom: -120px;
  width: 210px;
  height: 210px;
}

.workbench-section {
  margin-top: 20px;
  padding: 24px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow:
    0 18px 40px rgba(15, 23, 42, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.workbench-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 900;
    color: #0f172a;
  }

  p {
    margin: 7px 0 0;
    color: #64748b;
    font-size: 13px;
  }
}

.workbench-section__count {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  font-size: 13px;
  font-weight: 800;
}

.workbench-section__filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.workbench-menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.workbench-menu-card {
  display: flex;
  align-items: center;
  gap: 15px;
  min-height: 104px;
  padding: 18px;
  border: 1px solid rgba(203, 213, 225, 0.76);
  border-radius: 18px;
  text-align: left;
  background:
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.055);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(96, 165, 250, 0.76);
    box-shadow: 0 18px 34px rgba(37, 99, 235, 0.14);
  }
}

.workbench-menu-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border-radius: 17px;
  color: #ffffff;
  font-size: 24px;
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.18);

  &.is-tone-0 { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
  &.is-tone-1 { background: linear-gradient(135deg, #4f46e5, #7c3aed); }
  &.is-tone-2 { background: linear-gradient(135deg, #0ea5e9, #0891b2); }
  &.is-tone-3 { background: linear-gradient(135deg, #10b981, #059669); }
  &.is-tone-4 { background: linear-gradient(135deg, #f59e0b, #d97706); }
  &.is-tone-5 { background: linear-gradient(135deg, #475569, #1e293b); }
}

.workbench-menu-card__body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 8px;

  strong {
    overflow: hidden;
    color: #0f172a;
    font-size: 16px;
    font-weight: 900;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    overflow: hidden;
    color: #64748b;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.workbench-menu-card__arrow {
  color: #94a3b8;
  font-size: 18px;
}

// ============================================================
// 功能分组 + tone 配色卡片（用户权限=蓝 / 报告业务=粉 / 系统配置=绿 / 其他=中性）
// 配色取自项目主题变量体系，降饱和、与主题蓝紫主色协调
// ============================================================

// 分组行：左侧分组标题 + 右侧横向卡片（flex-wrap 自适应）
.workbench-group-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px dashed #e2e8f0;

  &:last-child {
    border-bottom: none;
  }

  &--favorite {
    padding: 12px 0;
  }
}

// 分组标题（色标圆点 + 文字）
.workbench-group-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-width: 132px;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 800;
  position: sticky;
  top: 0;

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex-shrink: 0;
  }

  // tone 配色：分组标签底色 + 文字色 + 圆点色
  &.is-blue {
    background: #eff6ff;
    color: #2f5cff;
    .workbench-group-title__dot { background: #4f7cff; }
  }
  &.is-pink {
    background: #fdf2f8;
    color: #db2777;
    .workbench-group-title__dot { background: #ec4899; }
  }
  &.is-green {
    background: #ecfdf5;
    color: #059669;
    .workbench-group-title__dot { background: #10b981; }
  }
  &.is-neutral {
    background: #f1f5f9;
    color: #475569;
    .workbench-group-title__dot { background: #64748b; }
  }
}

// 同组卡片容器（横向排列、自适应换行）
.workbench-entry-cards {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

// 轻量入口卡片
.workbench-entry-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 168px;
  padding: 12px 16px;
  border: 1px solid rgba(203, 213, 225, 0.7);
  border-radius: 14px;
  text-align: left;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    color: #ffffff;
    font-size: 18px;
  }

  &__body {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 3px;

    strong {
      overflow: hidden;
      color: #0f172a;
      font-size: 14px;
      font-weight: 800;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      overflow: hidden;
      color: #94a3b8;
      font-size: 12px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__arrow {
    color: #cbd5e1;
    font-size: 14px;
    flex-shrink: 0;
  }

  // tone 配色：icon 渐变 + hover 边框/阴影（同组同色）
  &.is-blue {
    .workbench-entry-card__icon { background: linear-gradient(135deg, #4f7cff, #6b9cff); }
    &:hover { border-color: rgba(79, 124, 255, 0.6); box-shadow: 0 12px 24px rgba(79, 124, 255, 0.18); }
  }
  &.is-pink {
    .workbench-entry-card__icon { background: linear-gradient(135deg, #a855f7, #ec4899); }
    &:hover { border-color: rgba(236, 72, 153, 0.5); box-shadow: 0 12px 24px rgba(236, 72, 153, 0.16); }
  }
  &.is-green {
    .workbench-entry-card__icon { background: linear-gradient(135deg, #10b981, #34d399); }
    &:hover { border-color: rgba(16, 185, 129, 0.55); box-shadow: 0 12px 24px rgba(16, 185, 129, 0.16); }
  }
  &.is-neutral {
    .workbench-entry-card__icon { background: linear-gradient(135deg, #64748b, #94a3b8); }
    &:hover { border-color: rgba(100, 116, 139, 0.55); box-shadow: 0 12px 24px rgba(100, 116, 139, 0.14); }
  }
}

// ===== 常用功能区块 =====
.workbench-section--favorite {
  position: relative;
}

.workbench-favorite-list {
  margin-top: -4px;
}

.workbench-favorite-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 130px;
  border-radius: 16px;
  color: #94a3b8;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  text-align: center;

  i {
    font-size: 32px;
    color: #cbd5e1;
  }

  p {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }
}

// ===== 编辑常用弹框（append-to-body，需穿透 scoped） =====
::v-deep .favorite-edit-dialog {
  border-radius: 16px;

  .el-dialog__header {
    padding: 18px 22px 12px;
    border-bottom: 1px solid #f1f5f9;
  }

  .el-dialog__title {
    font-size: 17px;
    font-weight: 800;
    color: #0f172a;
  }

  .el-dialog__body {
    padding: 18px 22px 4px;
    max-height: 62vh;
    overflow-y: auto;
  }

  .el-dialog__footer {
    padding: 12px 22px 18px;
    border-top: 1px solid #f1f5f9;
  }
}

.favorite-edit-body {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.favorite-edit-section {
  &__title {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
  }

  &__hint {
    font-size: 12px;
    font-weight: 500;
    color: #94a3b8;
  }
}

.favorite-edit-group {
  margin-bottom: 14px;

  &__title {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 10px;

    &.is-blue { background: #eff6ff; color: #2f5cff; }
    &.is-pink { background: #fdf2f8; color: #db2777; }
    &.is-green { background: #ecfdf5; color: #059669; }
    &.is-neutral { background: #f1f5f9; color: #475569; }
  }

  &__checks {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 18px;
  }
}

.favorite-edit-check {
  margin-right: 0 !important;
  margin-bottom: 0 !important;
}

.favorite-edit-sort {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  max-height: 220px;
  overflow-y: auto;
}

.favorite-edit-sort-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eef2f6;

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 800;
    color: #4f7cff;
    background: #eff6ff;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__ops {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
}

.favorite-edit-empty {
  padding: 18px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
}

.favorite-edit-footer {
  text-align: right;
}

// ===== 统计卡片 =====
.workbench-status-grid {
  display: grid;
  // 用户端/管理员端均为 6 张卡，统一 6 列
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
}

.workbench-status-card {
  position: relative;
  min-height: 148px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid rgba(203, 213, 225, 0.72);
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06);
  overflow: hidden;

  // 可点击卡片：手型光标 + hover 上浮 + 右上角跳转角标
  &.is-clickable {
    cursor: pointer;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 18px 36px rgba(37, 99, 235, 0.14);
      border-color: rgba(79, 124, 255, 0.5);
    }

    // 右上角跳转提示箭头（纯 CSS，不依赖字体码）
    &::before {
      content: '';
      position: absolute;
      right: 14px;
      top: 16px;
      width: 7px;
      height: 7px;
      border-top: 2px solid #cbd5e1;
      border-right: 2px solid #cbd5e1;
      transform: rotate(45deg);
      z-index: 1;
      transition: border-color 0.16s ease;
    }

    &:hover::before {
      border-color: #4f7cff;
    }
  }

  &::after {
    content: '';
    position: absolute;
    right: -36px;
    top: -44px;
    width: 112px;
    height: 112px;
    border-radius: 999px;
  }

  &.is-loading {
    background:
      linear-gradient(90deg, rgba(241, 245, 249, 0.65), rgba(226, 232, 240, 0.92), rgba(241, 245, 249, 0.65));
    background-size: 220% 100%;
    animation: workbench-loading 1.2s ease infinite;
  }

  // ----- 用户端配色 -----
  &.is-unSubmit {
    &::after { background: rgba(245, 158, 11, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #f59e0b, #d97706); }
  }
  &.is-submit {
    &::after { background: rgba(37, 99, 235, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #2563eb, #4f46e5); }
  }
  &.is-audit {
    &::after { background: rgba(79, 70, 229, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #4f46e5, #7c3aed); }
  }
  &.is-auditModify {
    &::after { background: rgba(239, 68, 68, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #ef4444, #dc2626); }
  }
  &.is-generate {
    &::after { background: rgba(16, 185, 129, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #10b981, #059669); }
  }
  // 我的报告（汇总卡）：主题蓝紫渐变，突出显示
  &.is-myReport {
    &::after { background: rgba(79, 124, 255, 0.1); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #4f7cff, #7c3aed); }
  }

  // ----- 管理员端配色 -----
  &.is-adminUnSubmit {
    &::after { background: rgba(245, 158, 11, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #f59e0b, #d97706); }
  }
  &.is-adminSubmit {
    &::after { background: rgba(37, 99, 235, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
  }
  &.is-adminAudit {
    &::after { background: rgba(79, 70, 229, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #4f46e5, #7c3aed); }
  }
  &.is-adminGenerate {
    &::after { background: rgba(16, 185, 129, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #10b981, #059669); }
  }
  &.is-adminAvgTime {
    &::after { background: rgba(14, 165, 233, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #0ea5e9, #0284c7); }
  }
  &.is-adminFeedback {
    &::after { background: rgba(168, 85, 247, 0.08); }
    .workbench-status-card__icon { background: linear-gradient(135deg, #a855f7, #7c3aed); }
  }
}

.workbench-status-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  font-size: 20px;
}

.workbench-status-card__label {
  margin-top: 16px;
  color: #475569;
  font-size: 13px;
  font-weight: 800;
}

.workbench-status-card__value {
  margin-top: 6px;
  color: #0f172a;
  font-size: 34px;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.workbench-status-card__unit {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-left: 2px;
}

.workbench-status-card__desc {
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
}

// ===== 任务列表区 =====
.workbench-task-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.workbench-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 112px;
  border-radius: 16px;
  color: #64748b;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  font-weight: 700;
}

.workbench-empty--error {
  color: #be123c;
  background: #fff1f2;
  border-color: #fecdd3;
}

.workbench-empty--tasks {
  min-height: 80px;
}

@keyframes workbench-loading {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media screen and (max-width: 1280px) {
  .workbench-status-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .workbench-todo-layout {
    flex-direction: column;
  }

  .workbench-todo-cards {
    flex-direction: row;
    min-width: unset;
  }

  .workbench-todo-card {
    flex: 1;
    min-height: 90px;
  }

  .workbench-duo-layout {
    grid-template-columns: 1fr;
  }
}

// ===== 待办提醒 =====
.workbench-todo-layout {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}

.workbench-todo-cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 160px;
  flex-shrink: 0;
}

.workbench-todo-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 110px;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid rgba(203, 213, 225, 0.72);
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.055);
  text-align: center;

  i {
    font-size: 26px;
  }

  &.is-today {
    i { color: #2563eb; }
    .workbench-todo-card__num { color: #1d4ed8; }
    border-left: 3px solid #2563eb;
  }

  &.is-overdue {
    i { color: #ef4444; }
    .workbench-todo-card__num { color: #dc2626; }
    border-left: 3px solid #ef4444;
  }
}

.workbench-todo-card__num {
  font-size: 32px;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.workbench-todo-card__text {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.workbench-todo-table {
  flex: 1;
  min-width: 0;
}

// ===== 双列并排布局 =====
.workbench-duo-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 20px;
}

.workbench-chart-container {
  width: 100%;
  height: 260px;
}

// ===== 排行榜 =====
.workbench-section--rank {
  .workbench-section__header {
    margin-bottom: 0;
  }
}

.workbench-rank-tabs {
  border: none;
  box-shadow: none;

  ::v-deep .el-tabs__header {
    background: transparent;
    border-bottom: 2px solid #e2e8f0;
    margin-bottom: 0;
  }

  ::v-deep .el-tabs__item {
    height: 38px;
    line-height: 38px;
    font-size: 13px;
    font-weight: 700;
    color: #64748b;
    border: none !important;

    &.is-active {
      color: #1d4ed8;
    }
  }

  ::v-deep .el-tabs__content {
    padding: 0;
  }
}

.workbench-rank-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
}

.workbench-rank-item {
  display: flex;
  align-items: center;
  padding: 9px 12px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
}

.workbench-rank-item__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  background: #f1f5f9;
  margin-right: 10px;
  flex-shrink: 0;

  &.is-top {
    color: #ffffff;
  }

  &.is-top-1 {
    background: linear-gradient(135deg, #f59e0b, #d97706);
  }

  &.is-top-2 {
    background: linear-gradient(135deg, #64748b, #475569);
  }

  &.is-top-3 {
    background: linear-gradient(135deg, #b45309, #92400e);
  }
}

.workbench-rank-item__name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workbench-rank-item__count {
  flex-shrink: 0;
  margin-left: 12px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  background: #eff6ff;
}

.workbench-rank-more {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
}

// 弹框内排名前三高亮
.rank-dialog-top {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

@media screen and (max-width: 768px) {
  .dashboard-workbench {
    padding: 6px 10px 18px;
  }

  .workbench-hero,
  .workbench-section__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .workbench-hero {
    padding: 26px;
  }

  .workbench-hero__title {
    font-size: 25px;
  }

  .workbench-hero__actions {
    width: 100%;
  }

  .workbench-section__filters {
    width: 100%;
    flex-wrap: wrap;
  }

  .workbench-status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  // 分组行：窄屏纵向排列，标题不再吸顶
  .workbench-group-row {
    flex-direction: column;
    gap: 10px;
  }

  .workbench-group-title {
    min-width: unset;
    position: static;
  }
}
</style>
